import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  Grid,
  Card,
  CardContent,
  Button,
  Switch,
  FormControlLabel,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import {
  Lock,
  Security as SecurityIcon,
  History,
  Edit,
  Delete,
  CheckCircle,
  Cancel,
  LocationOn,
  Person,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const Security = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();

  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // OTP verification state
  const [otp, setOtp] = useState('');

  // Password visibility states
  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false,
  });

 

  const handleChangePassword = async () => {
    // Validation
    if (!passwordForm.oldPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      enqueueSnackbar('Please fill in all fields', { variant: 'warning' });
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      enqueueSnackbar('New passwords do not match', { variant: 'error' });
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      enqueueSnackbar('New password must be at least 6 characters long', { variant: 'warning' });
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/api/users/change-password', {
        oldPassword: passwordForm.oldPassword,
        newPassword: passwordForm.newPassword,
        confirmPassword: passwordForm.confirmPassword,
      });

      if (response.data.success) {
        enqueueSnackbar('OTP sent to your email. Please check your inbox.', { variant: 'success' });
        setChangePasswordOpen(false);
        setOtpDialogOpen(true);
      } else {
        enqueueSnackbar(response.data.message || 'Failed to initiate password change', { variant: 'error' });
      }
    } catch (error) {
      console.error('Change password error:', error);
      enqueueSnackbar(
        error.response?.data?.message || 'Failed to change password. Please try again.',
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp.trim()) {
      enqueueSnackbar('Please enter the OTP', { variant: 'warning' });
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/api/users/verify-password-change-otp', {
        email: user?.email,
        otp: otp.trim(),
      });

      if (response.data.success) {
        enqueueSnackbar('Password changed successfully!', { variant: 'success' });
        setOtpDialogOpen(false);
        setOtp('');
        setPasswordForm({
          oldPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
      } else {
        enqueueSnackbar(response.data.message || 'Failed to verify OTP', { variant: 'error' });
      }
    } catch (error) {
      console.error('Verify OTP error:', error);
      enqueueSnackbar(
        error.response?.data?.message || 'Failed to verify OTP. Please try again.',
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordVisibilityToggle = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleOtpDialogClose = () => {
    setOtpDialogOpen(false);
    setOtp('');
    // Reset password form when OTP dialog is closed without completion
    setPasswordForm({
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
  };

  const loginHistory = [
    { device: 'Chrome on Windows', location: 'Gurugram', locationIcon: <LocationOn />, time: '2 hours ago', suspicious: false },
    { device: 'Safari on iPhone', location: 'Delhi', locationIcon: <LocationOn />, time: '1 day ago', suspicious: false },
    { device: 'Unknown Device', location: 'User', locationIcon: <Person />, time: '3 days ago', suspicious: true },
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Security Settings
      </Typography>

      <Grid container spacing={3}>
        {/* Password Management */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Lock color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Password Management</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                Keep your password strong and update it regularly to protect your account.
              </Typography>
              
              <Button
                variant="outlined"
                startIcon={<Edit />}
                onClick={() => setChangePasswordOpen(true)}
                fullWidth
              >
                Change Password
              </Button>
            </CardContent>
          </Card>
        </Grid>

        

        
            

      
        <Grid item xs={12}>
          <Card elevation={2}>

          </Card>
        </Grid>
      </Grid>

      {/* Change Password Dialog */}
      <Dialog open={changePasswordOpen} onClose={() => setChangePasswordOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Current Password"
            type={showPasswords.old ? 'text' : 'password'}
            fullWidth
            variant="outlined"
            value={passwordForm.oldPassword}
            onChange={(e) => setPasswordForm(prev => ({ ...prev, oldPassword: e.target.value }))}
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => handlePasswordVisibilityToggle('old')}
                    edge="end"
                    size="small"
                  >
                    {showPasswords.old ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            margin="dense"
            label="New Password"
            type={showPasswords.new ? 'text' : 'password'}
            fullWidth
            variant="outlined"
            value={passwordForm.newPassword}
            onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => handlePasswordVisibilityToggle('new')}
                    edge="end"
                    size="small"
                  >
                    {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            margin="dense"
            label="Confirm New Password"
            type={showPasswords.confirm ? 'text' : 'password'}
            fullWidth
            variant="outlined"
            value={passwordForm.confirmPassword}
            onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => handlePasswordVisibilityToggle('confirm')}
                    edge="end"
                    size="small"
                  >
                    {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangePasswordOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleChangePassword}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Sending OTP...' : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* OTP Verification Dialog */}
      <Dialog open={otpDialogOpen} onClose={handleOtpDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Verify OTP</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            We've sent a 6-digit OTP to your email ({user?.email}). Please enter it below to complete the password change.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Enter OTP"
            type="text"
            fullWidth
            variant="outlined"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            inputProps={{
              maxLength: 6,
              pattern: '[0-9]*',
            }}
            helperText="Enter the 6-digit code sent to your email"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleOtpDialogClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleVerifyOtp}
            variant="contained"
            disabled={loading || !otp.trim()}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Verifying...' : 'Verify & Change Password'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Security;